
import React, { useEffect, useMemo, useState } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { LoadingSpinner } from "components/common";
import { MdPlace } from "react-icons/md";
import { debounce } from "helpers/debounce";
import PropTypes from 'prop-types'

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const GoogleMapAutoCompletePlaceDebounce = ({ setAddress, defaultAddress, isRequired=false }) => {
  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading
  } = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const [inputValue, setInputValue] = useState(defaultAddress || '');
  const [selectedItem, setSelectedItem] = useState(null);
  const [shouldFetchPredictions, setShouldFetchPredictions] = useState(false);

  const debouncedGetPlacePredictions = useMemo(
    () => debounce((input) => {
      getPlacePredictions({ input });
    }, 500),
    // one debounce instance for the component's lifetime: recreating it would orphan the
    // pending timer and fire stale predictions alongside new ones
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (defaultAddress) {
      setInputValue(defaultAddress);
      setShouldFetchPredictions(false);
    }
  }, [defaultAddress]);

  useEffect(() => {
    if (shouldFetchPredictions && inputValue && !selectedItem) {
      debouncedGetPlacePredictions(inputValue);
    }
  }, [inputValue, selectedItem, shouldFetchPredictions, debouncedGetPlacePredictions]);

  const handleInputChange = (evt) => {
    setInputValue(evt.target.value);
    setSelectedItem(null);
    setShouldFetchPredictions(true);
  };

  const handleSelected = (item) => {
    setSelectedItem(item);
    setInputValue(item.description);
    setShouldFetchPredictions(false);

    // Set description immediately so validation doesn't fail on fast submits
    setAddress({ description: item.description });

    if (placesService) {
      placesService.getDetails(
        {
          placeId: item.place_id,
          fields: ['address_components']
        },
        (placeDetails) => {
          const addressComponents = placeDetails?.address_components;
          if (addressComponents) {
            const address = { description: item.description };
            addressComponents.forEach(component => {
              const types = component.types;

              if (types.includes('street_number')) {
                address.street_number = component.long_name;
              }
              else if (types.includes('route')) {
                address.route = component.long_name;
              }
              else if (types.includes('sublocality_level_1')) { //ward
                address.ward = component.long_name;
              } else if (types.includes('administrative_area_level_2') || types.includes('locality')) { //ward/district
                address.district = component.long_name;
              }
              else if (types.includes('administrative_area_level_1')) { //city/province
                address.city_province = component.long_name;
              } else if (types.includes('country')) {
                //address.country = component.long_name;
                address.country = component.short_name;
              }
            });
            setAddress(address);
          }
        }
      );
    }
  };

  return (
    <>
      <input
        placeholder="Enter an address"
        className={`${INPUT_CLASSES} ${isPlacePredictionsLoading ? 'tw-border-red-500' : 'tw-border-slate-300'}`}
        autoComplete="new-address"
        value={inputValue}
        onChange={handleInputChange}
        required={isRequired}
      />
      {isPlacePredictionsLoading && <LoadingSpinner size="sm" />}
      {!selectedItem && placePredictions.length > 0 && (
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-mt-1 tw-border tw-border-solid tw-border-slate-200 tw-rounded-md tw-bg-white tw-shadow-sm tw-divide-y tw-divide-slate-100">
          {placePredictions.map(item => (
            <li
              key={item.place_id}
              className="tw-px-3 tw-py-2 tw-text-sm tw-cursor-pointer hover:tw-bg-slate-50"
              onClick={() => handleSelected(item)}
            >
              <MdPlace /> {item.description}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

GoogleMapAutoCompletePlaceDebounce.propTypes = {
  setAddress: PropTypes.func,
  defaultAddress: PropTypes.string,
  isRequired: PropTypes.bool
};

export default GoogleMapAutoCompletePlaceDebounce;
